import { z } from 'zod';
import { IsoDateTime, PageQuery, Uuid } from './common.js';
/**
 * Tamper-evident signed agreements (module `agreements`, docs/modules/agreements.md, ADR 0012). Owner: task S8.
 */
const Sha256Hex = z.string().regex(/^[0-9a-f]{64}$/);
/** pending = accepted, PDF not made yet; sealed = PDF made, signed and chained; redacted = personal details removed (retention or erasure), chain entry kept. */
export const AgreementStatus = z.enum(['pending', 'sealed', 'failed', 'redacted']);
export const Agreement = z.object({
    id: Uuid,
    /** Position in the tenant's chain, from 1. Null until sealed. */
    seq: z.number().int().nullable(),
    visitId: Uuid.nullable(),
    noticeId: Uuid,
    noticeVersionId: Uuid,
    noticeVersion: z.number().int(),
    noticeTitle: z.string(),
    /** Null once redacted. */
    personName: z.string().nullable(),
    siteName: z.string(),
    groupName: z.string(),
    deviceName: z.string().nullable(),
    acceptedAt: IsoDateTime,
    hasSignature: z.boolean(),
    status: AgreementStatus,
    /** Why the last sealing attempt failed (an i18n key), while the agreement is pending or failed. */
    errorKey: z.string().nullable().optional(),
    pdfSha256: Sha256Hex.nullable(),
    chainHash: Sha256Hex.nullable(),
    signingKeyId: Uuid.nullable(),
    sealedAt: IsoDateTime.nullable(),
    redactedAt: IsoDateTime.nullable(),
});
export const AgreementListQuery = PageQuery.extend({
    visitId: Uuid.optional(),
    siteId: Uuid.optional(),
    noticeId: Uuid.optional(),
    status: AgreementStatus.optional(),
});
export const VerifyProblem = z.object({ seq: z.number().int().nullable(), messageKey: z.string() });
/** POST /agreements/:id/verify (agreements.view): the portal's Verify button. */
export const AgreementVerifyResult = z.object({
    agreementId: Uuid,
    /** The stored PDF still hashes to pdfSha256. Null when redacted (no PDF kept). */
    fingerprintMatches: z.boolean().nullable(),
    /** Every link from seq 1 to the chain head recomputes, with no gaps. */
    chainUnbroken: z.boolean(),
    /** The PDF's embedded signature checks against the CloudSign certificate. Null when redacted. */
    signatureValid: z.boolean().nullable(),
    /** The latest daily timestamp covering this entry, when timestamping is on. */
    timestampedAt: IsoDateTime.nullable(),
    checkedThroughSeq: z.number().int(),
    problems: z.array(VerifyProblem),
});
/** POST /agreements/public/verify (public, multipart PDF upload): reveals no personal data. */
export const PublicVerifyResult = z.object({
    /** The file's fingerprint matches a sealed CloudSign agreement. */
    found: z.boolean(),
    sealedAt: IsoDateTime.nullable(),
    /** The embedded signature is valid and made by a CloudSign signing key. */
    signatureValid: z.boolean(),
    /** The owning organisation's chain passed its latest nightly check. Null when not found. */
    chainVerified: z.boolean().nullable(),
    chainVerifiedAt: IsoDateTime.nullable(),
});
export const AgreementsTenantSettings = z.object({
    /** Make, sign and chain a PDF for every accepted notice. */
    enabled: z.boolean().default(true),
    timestamping: z
        .object({
        /** Daily RFC 3161 timestamp of the chain head (needs the platform TSA URL). */
        enabled: z.boolean().default(false),
    })
        .default({ enabled: false }),
});
export const AgreementsPlatformSettings = z.object({
    /** RFC 3161 timestamp service URL. Empty = timestamping unavailable. */
    tsaUrl: z.union([z.literal(''), z.url()]).default(''),
    /** Seconds between nightly-style chain checks per tenant; 0 = only on demand. */
    chainCheckHours: z.number().int().min(0).max(168).default(24),
});
/** GET /agreements/chain (agreements.view): the end of the tenant's chain and the latest check. lastSeq 0 = nothing sealed yet. */
export const ChainStatus = z.object({
    lastSeq: z.number().int().min(0),
    lastHash: Sha256Hex.nullable(),
    /** When the chain was last checked end to end (nightly job or a Verify); null = never. */
    verifiedAt: IsoDateTime.nullable(),
    verifiedOk: z.boolean().nullable(),
    /** The latest RFC 3161 timestamp of the chain head; null when timestamping is off or has not run. */
    timestampedAt: IsoDateTime.nullable(),
});
//# sourceMappingURL=agreements.js.map